import { Parameters } from "./parameters";

class LogoCompositor {
    private compositionCanvas: HTMLCanvasElement;
    private compositionContext: CanvasRenderingContext2D;
    private logoImage: HTMLImageElement | null = null;
    private backgroundImage: HTMLImageElement | null = null;
    private currentViewportSize: { width: number; height: number } = { width: 0, height: 0 };
    private isEnabled: boolean = false;
    private logoLoaded: boolean = false;
    private backgroundLoaded: boolean = false;
    
    // Cache the canvas element to avoid repeated DOM queries
    private cachedCanvas: HTMLCanvasElement | null = null;

    constructor() {
        this.compositionCanvas = document.createElement("canvas");
        this.compositionContext = this.compositionCanvas.getContext("2d", { willReadFrequently: true })!;
        
        // Listen for viewport size changes
        window.addEventListener('resize', () => {
            this.handleViewportChange();
        });
        
        // Load background image
        this.loadBackgroundImage();
    }

    public setEnabled(enabled: boolean): void {
        this.isEnabled = enabled;
        if (enabled) {
            // Check if both images are ready and generate composition
            this.checkAndGenerateComposition();
        }
    }

    public isCompositorEnabled(): boolean {
        return this.isEnabled;
    }

    private loadBackgroundImage(): void {
        const bgImage = new Image();
        bgImage.onload = () => {
            // console.log('Background image loaded successfully');
            this.backgroundImage = bgImage;
            this.backgroundLoaded = true;
            // Check if both images are ready and generate composition
            this.checkAndGenerateComposition();
        };
        bgImage.onerror = (error) => {
            console.error('Failed to load background image:', error);
            this.backgroundLoaded = true; // Mark as loaded even if failed
            this.checkAndGenerateComposition();
        };
        bgImage.src = './resources/background.jpg';
    }

    private checkAndGenerateComposition(): void {
        // Only generate composition if both images are loaded (or failed to load) and compositor is enabled
        if (this.logoLoaded && this.backgroundLoaded && this.isEnabled) {
            this.handleViewportChange();
        }
    }

    public loadLogo(logoUrl: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => {
                // console.log('Logo loaded successfully:', logoUrl);
                this.logoImage = image;
                this.logoLoaded = true;
                // Check if both images are ready and generate composition
                this.checkAndGenerateComposition();
                resolve();
            };
            image.onerror = (error) => {
                console.error('Failed to load logo:', logoUrl, error);
                this.logoLoaded = true; // Mark as loaded even if failed
                this.checkAndGenerateComposition();
                reject(error);
            };
            image.src = logoUrl;
        });
    }

    private handleViewportChange(): void {
        if (!this.isEnabled) return;
        
        // Cache canvas element to avoid repeated DOM queries
        if (!this.cachedCanvas) {
            this.cachedCanvas = document.getElementById('canvas') as HTMLCanvasElement;
        }
        
        if (!this.cachedCanvas) return;

        const newSize = {
            width: this.cachedCanvas.clientWidth,
            height: this.cachedCanvas.clientHeight
        };

        // Only regenerate if size actually changed
        if (newSize.width !== this.currentViewportSize.width || 
            newSize.height !== this.currentViewportSize.height) {
            this.currentViewportSize = newSize;
            this.generateComposition();
        }
    }

    private drawBackgroundImage(width: number, height: number): void {
        if (!this.backgroundImage) return;
        
        const ctx = this.compositionContext;
        
        // Calculate scaling to fill the entire canvas (cover mode)
        const imgAspect = this.backgroundImage.width / this.backgroundImage.height;
        const canvasAspect = width / height;
        
        let drawWidth, drawHeight, drawX, drawY;
        
        if (imgAspect > canvasAspect) {
            // Image is wider - scale to height and center horizontally
            drawHeight = height;
            drawWidth = height * imgAspect;
            drawX = (width - drawWidth) / 2;
            drawY = 0;
        } else {
            // Image is taller - scale to width and center vertically
            drawWidth = width;
            drawHeight = width / imgAspect;
            drawX = 0;
            drawY = (height - drawHeight) / 2;
        }
        
        // Draw background image to fill canvas
        ctx.drawImage(this.backgroundImage, drawX, drawY, drawWidth, drawHeight);
    }

    private generateFunkyGradient(width: number, height: number): void {
        const ctx = this.compositionContext;
        
        // Create a funky multi-stop radial gradient
        const centerX = width / 2;
        const centerY = height / 2;
        const maxRadius = Math.sqrt(centerX * centerX + centerY * centerY);
        
        const gradient = ctx.createRadialGradient(
            centerX, centerY, 0,
            centerX, centerY, maxRadius
        );
        
        // Funky gradient stops with vibrant colors
        gradient.addColorStop(0, '#ff6b6b');    // Red
        gradient.addColorStop(0.2, '#4ecdc4');  // Teal
        gradient.addColorStop(0.4, '#45b7d1');  // Blue
        gradient.addColorStop(0.6, '#96ceb4');  // Green
        gradient.addColorStop(0.8, '#feca57');  // Yellow
        gradient.addColorStop(1, '#ff9ff3');    // Pink
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
        
        // Add some funky diagonal stripes with low opacity
        this.addDiagonalPattern(width, height);
    }

    private addDiagonalPattern(width: number, height: number): void {
        const ctx = this.compositionContext;
        
        ctx.save();
        ctx.globalAlpha = 0.1;
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        
        // Draw diagonal lines
        const spacing = 50;
        for (let i = -height; i < width + height; i += spacing) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i + height, height);
            ctx.stroke();
        }
        
        // Draw diagonal lines in the other direction
        ctx.strokeStyle = '#000000';
        for (let i = -height; i < width + height; i += spacing * 1.5) {
            ctx.beginPath();
            ctx.moveTo(i, height);
            ctx.lineTo(i + height, 0);
            ctx.stroke();
        }
        
        ctx.restore();
    }

    private calculateLogoTransform(logoImg: HTMLImageElement, viewport: { width: number; height: number }) {
        // Determine if viewport is portrait
        const isPortrait = viewport.height > viewport.width;
        
        let scale: number;
        let scaledLogoWidth: number;
        let scaledLogoHeight: number;
        
        if (isPortrait) {
            // In portrait mode, logo will be rotated 90 degrees
            // So logo's width will become height, and logo's height will become width
            // Scale to fit both dimensions with some padding
            const scaleForWidth = (viewport.width * 0.8) / logoImg.height; // logo height becomes width after rotation
            const scaleForHeight = (viewport.height * 0.8) / logoImg.width; // logo width becomes height after rotation
            scale = Math.min(scaleForWidth, scaleForHeight);
        } else {
            // In landscape mode, scale based on width as before
            scale = (viewport.width * 0.9) / logoImg.width;
        }
        
        // Calculate scaled logo dimensions
        scaledLogoWidth = logoImg.width * scale;
        scaledLogoHeight = logoImg.height * scale;
        
        // Calculate final dimensions (after potential rotation)
        let finalWidth, finalHeight;
        if (isPortrait) {
            // When rotated 90 degrees, width becomes height and vice versa
            finalWidth = scaledLogoHeight;
            finalHeight = scaledLogoWidth;
        } else {
            finalWidth = scaledLogoWidth;
            finalHeight = scaledLogoHeight;
        }
        
        // Center positioning based on final dimensions
        const centerX = viewport.width / 2;
        const centerY = viewport.height / 2;
        
        return { 
            scale, 
            centerX,
            centerY,
            scaledWidth: scaledLogoWidth,
            scaledHeight: scaledLogoHeight,
            finalWidth,
            finalHeight,
            rotation: isPortrait ? Math.PI / 2 : 0 // 90 degrees in radians for portrait
        };
    }

    private generateComposition(): void {
        if (!this.isEnabled || !this.logoImage) {
            // console.log('Logo compositor not enabled or no logo image');
            return;
        }
        
        // console.log('Generating composition...', this.currentViewportSize);
        if (!this.logoImage || this.currentViewportSize.width === 0) return;

        const { width, height } = this.currentViewportSize;
        
        // Set canvas size to match viewport
        this.compositionCanvas.width = width;
        this.compositionCanvas.height = height;
        
        // Clear canvas
        this.compositionContext.clearRect(0, 0, width, height);
        
        // 1. Draw background image (fill screen) or fallback to gradient
        if (this.backgroundImage && Parameters.useBackgroundImage) {
            this.drawBackgroundImage(width, height);
        } else {
            this.generateFunkyGradient(width, height);
        }
        
        // 2. Calculate logo positioning
        const logoTransform = this.calculateLogoTransform(this.logoImage, { width, height });
        
        // 3. Draw logo with alpha blending and rotation if needed
        this.compositionContext.save();
        this.compositionContext.globalAlpha = 1.0; // Full opacity for logo
        
        if (logoTransform.rotation !== 0) {
            // Move to center, rotate, then draw from the center
            this.compositionContext.translate(logoTransform.centerX, logoTransform.centerY);
            this.compositionContext.rotate(logoTransform.rotation);
            // Draw from center (negative half-width and half-height)
            this.compositionContext.drawImage(
                this.logoImage,
                -logoTransform.scaledWidth / 2,
                -logoTransform.scaledHeight / 2,
                logoTransform.scaledWidth,
                logoTransform.scaledHeight
            );
        } else {
            // No rotation - draw normally from top-left corner
            const x = logoTransform.centerX - logoTransform.scaledWidth / 2;
            const y = logoTransform.centerY - logoTransform.scaledHeight / 2;
            this.compositionContext.drawImage(
                this.logoImage,
                x,
                y,
                logoTransform.scaledWidth,
                logoTransform.scaledHeight
            );
        }
        
        this.compositionContext.restore();
        
        // 4. Convert to ImageData and feed into reaction-diffusion system
        const imageData = this.compositionContext.getImageData(0, 0, width, height);
        this.triggerImageUpdate(imageData);
    }

    private triggerImageUpdate(imageData: ImageData): void {
        // Create a temporary image element to trigger the existing image upload system
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true })!;
        
        tempCanvas.width = imageData.width;
        tempCanvas.height = imageData.height;
        tempCtx.putImageData(imageData, 0, 0);
        
        // Convert to image and trigger the existing upload observers
        const tempImage = new Image();
        tempImage.onload = () => {
            // Trigger all the existing image upload observers
            for (const observer of Parameters.imageUploadObservers) {
                observer(tempImage);
            }
        };
        tempImage.src = tempCanvas.toDataURL();
    }

    public forceRegenerate(): void {
        this.generateComposition();
    }
}

// Export singleton instance
export const logoCompositor = new LogoCompositor();
